import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { fmtMoney } from "@/site/api";
import { track } from "@/site/analytics";

/** Shared UI for the metric calculators. URL-synced inputs => every result is a shareable link. */
export function useParams(defaults) {
  const [sp, setSp] = useSearchParams();
  const initial = useMemo(() => {
    const o = { ...defaults };
    Object.keys(defaults).forEach((k) => { const v = sp.get(k); if (v !== null && v !== "" && Number.isFinite(Number(v))) o[k] = Number(v); });
    return o;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [v, setV] = useState(initial);
  useEffect(() => {
    const next = new URLSearchParams();
    Object.entries(v).forEach(([k, x]) => { if (x !== defaults[k]) next.set(k, String(x)); });
    setSp(next, { replace: true });
  }, [v]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (x) => setV((s) => ({ ...s, [k]: x }));
  return [v, set];
}

export function Money({ id, label, value, onChange, min = 0, max = 100_000_000, step = 1000, hint }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div style={{ position: "relative" }}>
        <span style={{ position: "absolute", left: 11, top: 9, color: "var(--faint)", fontSize: 14 }}>$</span>
        <input id={id} className="input num" style={{ paddingLeft: 24 }} type="number" inputMode="numeric" min={min} max={max} step={step} value={value}
          onChange={(e) => onChange(Math.max(min, Math.min(max, Number(e.target.value) || 0)))} data-testid={id} />
      </div>
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

export function Pct({ id, label, value, onChange, min = 0, max = 100, step = 1, hint }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label} <span className="mono" style={{ color: "var(--faint)" }}>{value}%</span></label>
      <input id={id} className="range" type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={id} />
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

export function Num({ id, label, value, onChange, min = 0, max = 100000, step = 1, hint }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input id={id} className="input num" type="number" inputMode="numeric" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Math.max(min, Math.min(max, Number(e.target.value) || 0)))} data-testid={id} />
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

export function Kpi({ k, v, s, testid, tone }) {
  return (
    <div className="kpi"><div className="k">{k}</div><div className="v num" data-testid={testid} style={tone ? { color: tone } : undefined}>{v}</div>{s && <div className="s">{s}</div>}</div>
  );
}

export function Shell({ form, out, note }) {
  return (
    <div className="calc">
      <form className="calc__form" onSubmit={(e) => e.preventDefault()}>{form}</form>
      <div className="calc__out" aria-live="polite">{out}{note && <div className="calc__src">{note}</div>}</div>
    </div>
  );
}

export function ShareRow({ name }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(window.location.href); setCopied(true); track("calc_share", { name }); setTimeout(() => setCopied(false), 1800); } catch {} };
  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
      <button type="button" className="btn btn--ghost btn--sm" onClick={copy} data-testid="calc-copy-link" style={{ color: "#f7f6f3", borderColor: "#3d3934" }}>{copied ? "Link copied" : "Copy link to this result"}</button>
      <Link to="/pilot" className="btn btn--primary btn--sm" onClick={() => track("cta_click", { where: `calc_${name}` })}>See it on your real book →</Link>
    </div>
  );
}

export const money0 = (n) => fmtMoney(n, "USD", 0);
